/* usePoller — a small, reusable polling helper for the sportsbook's live surfaces.
 *
 * Why: several pollers (Sportsbook board, bet-slip quotes, event markets) silently swallowed
 * failures and kept the last-good data on screen with NO indication that updates had stopped.
 * That's the right default (never go blank on a transient blip) but it must be HONEST — a long
 * stall should surface a subtle "reconnecting…" state so a customer isn't staring at a frozen
 * board thinking it's fresh.
 *
 * This helper does NOT loosen any placement gate. It only tracks freshness and prevents overlapping
 * in-flight requests. Callers keep displaying their own last-good data exactly as before.
 *
 * usePoller(fn, { interval, staleAfter, enabled, deps })
 *   fn        : async () => any     — one poll. Resolves on success, throws on failure.
 *   interval  : ms between polls (default 1000)
 *   staleAfter: ms since last success after which `stale` flips true (default 6000)
 *   enabled   : when false, the poller is idle (no timer, status reset)
 *   deps      : dependency array — the loop restarts (and an immediate poll fires) when these change
 *
 * Returns { stale, failures, lastOkAt, refresh } where:
 *   stale     : true once `staleAfter` ms have elapsed with no successful poll (and we've seen ≥1 ok)
 *   failures  : consecutive failed polls since the last success
 *   lastOkAt  : epoch ms of the last successful poll (null before the first)
 *   refresh() : fire an out-of-band poll now (still guarded against overlap)
 *
 * Overlap guard: if a poll is still in flight when the next tick arrives, the tick is skipped — so a
 * slow/hung request can never stack up or let an older response land after a newer one.
 */
import { useEffect, useRef, useState, useCallback } from "react";

export function usePoller(fn, { interval = 1000, staleAfter = 6000, enabled = true, deps = [] } = {}) {
  const [stale, setStale] = useState(false);
  const [failures, setFailures] = useState(0);
  const [lastOkAt, setLastOkAt] = useState(null);

  const fnRef = useRef(fn);
  fnRef.current = fn;
  const inFlight = useRef(false);
  const lastOk = useRef(null);
  const aliveRef = useRef(true);

  const runOnce = useCallback(async () => {
    if (inFlight.current) return; // skip overlapping requests
    inFlight.current = true;
    try {
      await fnRef.current();
      if (!aliveRef.current) return;
      lastOk.current = Date.now();
      setLastOkAt(lastOk.current);
      setFailures(0);
      setStale(false);
    } catch {
      if (!aliveRef.current) return;
      setFailures((n) => n + 1);
    } finally {
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    aliveRef.current = true;
    if (!enabled) {
      // idle: clear any transient "reconnecting" state so a disabled poller never looks stalled
      setStale(false);
      setFailures(0);
      return () => { aliveRef.current = false; };
    }
    runOnce();
    const poll = setInterval(runOnce, interval);
    // independent, cheap staleness tick — flips `stale` once we're past staleAfter with no success
    const freshness = setInterval(() => {
      if (!aliveRef.current) return;
      if (lastOk.current == null) return; // never mark stale before the very first success
      setStale(Date.now() - lastOk.current > staleAfter);
    }, Math.min(1000, staleAfter));
    return () => {
      aliveRef.current = false;
      clearInterval(poll);
      clearInterval(freshness);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runOnce, interval, staleAfter, enabled, ...deps]);

  return { stale, failures, lastOkAt, refresh: runOnce };
}

// A tiny shared "reconnecting…" chip — honest, subtle, never blocks interaction. Callers render it
// when their poller reports `stale`. Kept here so every live surface uses the same affordance.
export function reconnectingChipProps() {
  return {
    "data-testid": "reconnecting-chip",
    className: "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold text-[#a1a1aa]",
    style: { background: "rgba(245,158,11,.10)", boxShadow: "inset 0 0 0 1px rgba(245,158,11,.30)" },
  };
}
